import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
// import { SERVICE_OPTIONS } from "../../data/serviceOptions";

const SERVICE_OPTIONS = [
  {
    value: "counselling",
    title: "Counselling",
    path: "counselling",
  },
  {
    value: "physiotherapy",
    title: "Physiotherapy",
    path: "physiotherapy",
  },
  {
    value: "nurse support",
    title: "Nurse Support",
    path: "nurse-support",
  },
  {
    value: "sas gym",
    title: "SAS Gym",
    path: "sas-gym",
  },
  {
    value: "relaxation room",
    title: "Relaxation Room",
    path: "relaxation-room",
  },
  {
    value: "happier healthier you",
    title: "Happier Healthier You",
    path: "happier-healthier-you",
  },
  {
    value: "gp referral",
    title: "GP Referral",
    path: "gp-referral",
  },
];

const ROUTINE_CANCER_CHECK_OPTIONS = [
  "Testicular cancer check",
  "Prostate cancer check",
  "Breast cancer check",
  "Smear test",
];

const FormFooterSection = ({
  services,
  routineCancerChecks,
  onRoutineCancerChecksChange,
  recommendedServices,
  onRecommendedServicesChange,
  additionalNotes,
  onAdditionalNotesChange,
  optOut,
  onOptOutChange,
  submitLabel,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const availableOptions = SERVICE_OPTIONS.filter(
    (option) => option.value !== services,
  );

  const selectedTitles = availableOptions
    .filter((option) => recommendedServices.includes(option.value))
    .map((option) => option.title)
    .join(", ");

  const toggleRecommendedService = (serviceValue, isChecked) => {
    const nextSelection = isChecked
      ? [...recommendedServices, serviceValue]
      : recommendedServices.filter((value) => value !== serviceValue);

    onRecommendedServicesChange(nextSelection);
  };

  const toggleRoutineCancerCheck = (checkValue, isChecked) => {
    const nextSelection = isChecked
      ? [...routineCancerChecks, checkValue]
      : routineCancerChecks.filter((value) => value !== checkValue);

    onRoutineCancerChecksChange(nextSelection);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <>
      <div className="rounded-2xl border border-sas-primary/20 bg-white px-4 py-4 shadow-sm mb-4">
        <label className="text-xs font-semibold uppercase tracking-[0.25em] text-sas-primary block">
          Routine Cancer Check Reminders
        </label>
        <p className="mt-2 text-xs md:text-sm text-slate-500 font-montserrat">
          Tick each routine cancer check discussed.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {ROUTINE_CANCER_CHECK_OPTIONS.map((option) => {
            const optionId = `routine-cancer-${option
              .toLowerCase()
              .replace(/\s+/g, "-")}`;
            const checked = routineCancerChecks.includes(option);

            return (
              <label
                key={option}
                htmlFor={optionId}
                className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2 hover:bg-sky-50 cursor-pointer"
              >
                <input
                  id={optionId}
                  type="checkbox"
                  className="h-4 w-4 rounded border border-slate-300 text-sas-primary focus:ring-2 focus:ring-sas-primary/30"
                  checked={checked}
                  onChange={(e) =>
                    toggleRoutineCancerCheck(option, e.target.checked)
                  }
                />
                <span className="text-sm font-semibold text-sas-primary-dark font-montserrat">
                  {option}
                </span>
              </label>
            );
          })}
        </div>
        <input
          type="hidden"
          name="routineCancerChecks"
          value={routineCancerChecks.join(",")}
        />
      </div>

      <div className="rounded-2xl border border-sas-primary/20 bg-white px-4 py-4 shadow-sm">
        <label
          className="text-xs font-semibold uppercase tracking-[0.25em] text-sas-primary block"
          htmlFor="services-recommended"
        >
          Services Recommended
        </label>
        <p className="mt-2 text-xs md:text-sm text-slate-500 font-montserrat">
          Open the dropdown and tick multiple services.
        </p>
        <div className="relative mt-3" ref={dropdownRef}>
          <button
            type="button"
            id="services-recommended"
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            className="w-full rounded-xl border border-slate-300 px-4 py-3 text-base font-semibold text-sas-primary-dark font-montserrat focus:outline-none focus:ring-2 focus:ring-sas-primary/30 bg-white cursor-pointer"
            onClick={() => setIsDropdownOpen((previous) => !previous)}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm md:text-base text-left font-semibold text-sas-primary-dark">
                  {recommendedServices.length > 0
                    ? `${recommendedServices.length} selected`
                    : "Select one or more services"}
                </p>
                <p className="mt-0.5 text-xs text-slate-500 truncate text-left">
                  {selectedTitles || "No services selected"}
                </p>
              </div>
              <span
                aria-hidden="true"
                className="text-sas-primary text-sm leading-none pt-1"
              >
                {isDropdownOpen ? "▲" : "▼"}
              </span>
            </div>
          </button>
          {isDropdownOpen ? (
            <div
              role="listbox"
              aria-label="Services Recommended options"
              className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white p-2 shadow-lg"
            >
              {availableOptions.map((option) => {
                const optionId = `recommended-${option.path}`;
                const checked = recommendedServices.includes(option.value);

                return (
                  <label
                    key={option.value}
                    htmlFor={optionId}
                    className="flex items-center gap-3 rounded-lg px-3 py-2 hover:bg-sky-50 cursor-pointer"
                  >
                    <input
                      id={optionId}
                      type="checkbox"
                      className="h-4 w-4 rounded border border-slate-300 text-sas-primary focus:ring-2 focus:ring-sas-primary/30"
                      checked={checked}
                      onChange={(e) =>
                        toggleRecommendedService(option.value, e.target.checked)
                      }
                    />
                    <span className="text-sm font-semibold text-sas-primary-dark font-montserrat">
                      {option.title}
                    </span>
                  </label>
                );
              })}
            </div>
          ) : null}
        </div>
        <input type="hidden" name="services" value={services} />
        <input
          type="hidden"
          name="recommendedServices"
          value={recommendedServices.join(",")}
        />
      </div>

      <div className="relative mt-8 rounded-2xl border border-sky-200 bg-white shadow-sm overflow-hidden">
        <h2 className="font-fatfrank text-lg md:text-xl text-sas-primary bg-sky-50 border-b border-sky-100 p-4">
          Additional Notes
        </h2>
        <div className="p-4">
          <textarea
            className="w-full rounded-xl border border-slate-200 px-4 py-3 font-semibold text-sas-primary-dark font-montserrat text-base focus:outline-none focus:ring-2 focus:ring-sas-primary/30"
            rows="6"
            value={additionalNotes}
            onChange={onAdditionalNotesChange}
          ></textarea>
        </div>
      </div>

      <div className="flex items-center mt-1 font-medium">
        <FontAwesomeIcon
          icon={faCircleExclamation}
          className="text-sas-primary "
        />{" "}
        <span className="text-sas-primary-dark pl-2 text-xs md:text-sm font-montserrat">
          Following your health screen please consider how making some changes
          to your lifestyle (however small) may assist in reducing future
          chronic health conditions such as heart disease, diabetes and
          dementia.
        </span>
      </div>

      <div className="relative z-10">
        <div className=" mt-4 font-medium">
          <span className="text-sas-primary-dark mt-8 block text-xs font-montserrat">
            Make a Difference (UK) Ltd (MAD) uses anonymised data for
            statistical and research purposes aimed at identifying broad
            regional health trends among educators.
          </span>
          <span className="text-sas-primary-dark mt-4 block text-xs font-montserrat">
            Our lawful bases for this processing under the UK GDPR are:
            <ul className="list-disc list-inside">
              <li>for our legitimate interests (Art. 6 (1)(f), and;</li>
              <li>for statistical and research purposes (Art. 9(2)(j)</li>
            </ul>
            If you do not wish your health data to be used for these purposes
            please tick the box below to opt out of the study. For more
            information about how MAD uses personal data please see the
            <a
              href="https://schoolwellbeing.com/privacy"
              className="underline ml-1"
            >
              Privacy Notice
            </a>
          </span>
          <span className="text-sas-primary-dark mt-8 block text-xs font-montserrat">
            <span className="pr-2">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border border-slate-300 text-sas-primary focus:ring-2 focus:ring-sas-primary/30"
                checked={optOut}
                id="opt-out"
                onChange={onOptOutChange}
              />
            </span>
            <label className="inline" htmlFor="opt-out">
              Tick this box if you do not want MAD to include your anonymised
              health data in its research projects.
            </label>
          </span>
        </div>
        <div className="flex justify-center relative z-10">
          <button
            className="bg-sas-primary w-full mt-8 hover:bg-sas-primary-dark text-white font-fatfrank py-2.5 px-4 rounded-lg focus:outline-none focus:shadow-outline text-center"
            type="submit"
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </>
  );
};

export default FormFooterSection;
